import React, { useState } from 'react';
import Swal from 'sweetalert2';

const WidgetRenderer = ({ widget, sectionId, columnId, isSelected, onSelect, onUpdate, onDelete, onOpenSettings }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [editValue, setEditValue] = useState({});
  const [accordionState, setAccordionState] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  
  const allowInlineEdit = (type) => {
    return ![
      'heading',
      'input-text',
      'input-email',
      'input-password',
      'input-number',
      'textarea',
      'select',
      'checkbox',
      'radio',
      'form'
    ].includes(type);
  };
  
  const getYoutubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };
  
  const getVimeoId = (url) => {
    const regExp = /vimeo\.com\/(?:channels\/|groups\/\d+\/|video\/|)(\d+)(?:[?/]|$)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };
  
  const getIconSymbol = (name) => {
    const icons = {
      star: '★',
      heart: '❤',
      arrow: '→',
      check: '✓',
      info: 'ℹ',
      facebook: 'f',
      twitter: '🐦',
      instagram: '📷',
      linkedin: 'in',
      youtube: '📺'
    };
    return icons[name] || '★';
  };
  
  const renderWidget = () => {
    if (!widget || !widget.type) {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Invalid widget</div>;
    }
    
    switch(widget.type) {
      // Basic Elements
      case 'heading':
        const content = widget.content || {};
        const HeadingTag = content.tag || 'h2';
        return (
          <HeadingTag 
            className="widget-heading"
            style={{ 
              textAlign: widget.styles?.textAlign || 'left',
              color: widget.styles?.color || '#333',
              fontSize: widget.styles?.fontSize || '24px',
              fontWeight: widget.styles?.fontWeight || 'bold',
              margin: 0
            }}
          >
            {content.text || 'Heading Text'}
          </HeadingTag>
        );
        
      case 'text':
      case 'paragraph':
        return (
          <div 
            className="widget-text"
            style={{ 
              textAlign: widget.styles?.textAlign || 'left',
              color: widget.styles?.color || '#666',
              fontSize: widget.styles?.fontSize || '16px',
              lineHeight: widget.styles?.lineHeight || '1.6'
            }}
          >
            {widget.content?.text || 'Text content'}
          </div>
        );
        
      case 'button':
        return (
          <a 
            href={widget.content?.link || '#'}
            className="widget-button"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              backgroundColor: widget.styles?.backgroundColor || '#007bff',
              color: widget.styles?.color || '#fff',
              textDecoration: 'none',
              borderRadius: `${widget.styles?.borderRadius || 4}px`,
              fontSize: widget.styles?.fontSize || '16px',
              textAlign: widget.styles?.textAlign || 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            {widget.content?.icon && <span className="button-icon" style={{ marginRight: '8px' }}>{widget.content.icon}</span>}
            {widget.content?.text || 'Button Text'}
          </a>
        );
        
      case 'link':
        return (
          <a 
            href={widget.content.url || '#'}
            target={widget.content.target || '_self'}
            className="widget-link"
            style={{
              color: widget.styles?.color || '#007bff',
              textDecoration: widget.styles?.textDecoration || 'underline',
              fontSize: widget.styles?.fontSize || '16px'
            }}
          >
            {widget.content.text}
          </a>
        );
        
      case 'quote':
        return (
          <blockquote 
            className="widget-quote"
            style={{
              borderLeft: `4px solid ${widget.styles?.borderColor || '#007bff'}`,
              padding: '10px 20px',
              margin: '20px 0',
              fontStyle: 'italic',
              color: widget.styles?.color || '#666'
            }}
          >
            <p style={{ margin: 0 }}>{widget.content.text}</p>
            {widget.content.author && (
              <footer style={{ marginTop: '10px', fontWeight: 'bold' }}>
                — {widget.content.author}
              </footer>
            )}
          </blockquote>
        );
        
      case 'list':
        const ListTag = widget.content.type === 'ordered' ? 'ol' : 'ul';
        return (
          <ListTag 
            className="widget-list"
            style={{
              color: widget.styles?.color || '#666',
              fontSize: widget.styles?.fontSize || '16px',
              paddingLeft: '20px'
            }}
          >
            {widget.content.items?.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ListTag>
        );
        
      // Media Elements
      case 'image':
        return (
          <div className="widget-image">
            <img 
              src={widget.content?.url || 'https://via.placeholder.com/400x200'} 
              alt={widget.content?.alt || 'Image'}
              style={{ width: '100%', height: 'auto', borderRadius: widget.styles?.borderRadius || 0 }}
            />
            {widget.content?.caption && (
              <p className="image-caption" style={{ textAlign: 'center', marginTop: '8px', color: '#666' }}>
                {widget.content.caption}
              </p>
            )}
          </div>
        );
        
      case 'video':
        const videoId = getYoutubeId(widget.content.url) || getVimeoId(widget.content.url);
        const embedUrl = getYoutubeId(widget.content.url) 
          ? `https://www.youtube.com/embed/${videoId}`
          : `https://player.vimeo.com/video/${videoId}`;
        return (
          <div className="widget-video">
            <iframe
              width="100%"
              height={widget.styles?.height || 400}
              src={embedUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Video player"
            ></iframe>
          </div>
        );
        
      case 'audio':
        return (
          <div className="widget-audio">
            <audio 
              controls={widget.content.controls}
              autoPlay={widget.content.autoplay}
              loop={widget.content.loop}
              style={{ width: '100%' }}
            >
              <source src={widget.content.url} />
              Your browser does not support the audio element.
            </audio>
          </div>
        );
        
      case 'gallery':
        return (
          <div className="widget-gallery" style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${widget.content.columns || 3}, 1fr)`,
            gap: `${widget.content.spacing || 10}px`
          }}>
            {widget.content.images?.map((img, index) => (
              <img key={index} src={img} alt={`Gallery ${index + 1}`} style={{ width: '100%', height: 'auto' }} />
            ))}
          </div>
        );
        
      case 'slider':
        return (
          <div className="widget-slider">
            {/* Simple slider implementation - you might want to use a library like Swiper here */}
            <div style={{ position: 'relative', overflow: 'hidden' }}>
              <div style={{ display: 'flex', transition: 'transform 0.3s ease' }}>
                {widget.content.images?.map((img, index) => (
                  <img key={index} src={img} alt={`Slide ${index + 1}`} style={{ width: '100%', flexShrink: 0 }} />
                ))}
              </div>
            </div>
          </div>
        );
        
      case 'map':
        return (
          <div className="widget-map">
            <iframe
              width="100%"
              height={widget.content.height || 400}
              frameBorder="0"
              style={{ border: 0 }}
              src={`https://maps.google.com/maps?q=${encodeURIComponent(widget.content.address)}&output=embed`}
              allowFullScreen
              title="Google Maps"
            ></iframe>
          </div>
        );
        
      // Layout Elements
      case 'divider':
        return (
          <hr 
            className="widget-divider"
            style={{
              border: 'none',
              borderTop: `${widget.content.weight || 2}px ${widget.content.style || 'solid'} ${widget.content.color || '#ddd'}`,
              width: `${widget.content.width || 100}%`,
              margin: '20px auto'
            }}
          />
        );
        
      case 'spacer':
        return <div style={{ height: widget.content.height || 50 }}></div>;
        
      case 'accordion':
        return (
          <div className="widget-accordion">
            {widget.content.items?.map((item, index) => (
              <div key={index} style={{ marginBottom: '10px' }}>
                <button
                  onClick={() => {
                    const newState = { ...accordionState };
                    newState[widget.id] = newState[widget.id] === index ? null : index;
                    setAccordionState(newState);
                  }}
                  style={{
                    width: '100%',
                    padding: '15px',
                    textAlign: 'left',
                    backgroundColor: '#f7f7f7',
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {item.title}
                </button>
                {accordionState[widget.id] === index && (
                  <div style={{ padding: '15px', border: '1px solid #ddd', borderTop: 'none' }}>
                    {item.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
        
      case 'tabs':
        return (
          <div className="widget-tabs">
            <div style={{ display: 'flex', borderBottom: '2px solid #ddd' }}>
              {widget.content.tabs?.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: activeTab === index ? '#fff' : 'transparent',
                    border: 'none',
                    borderBottom: activeTab === index ? `2px solid ${widget.styles?.activeColor || '#007bff'}` : 'none',
                    cursor: 'pointer',
                    color: activeTab === index ? widget.styles?.activeColor || '#007bff' : '#666'
                  }}
                >
                  {tab.title}
                </button>
              ))}
            </div>
            <div style={{ padding: '20px', border: '1px solid #ddd', borderTop: 'none' }}>
              {widget.content.tabs?.[activeTab]?.content}
            </div>
          </div>
        );
        
      // Dynamic Elements
      case 'counter':
        return (
          <div className="widget-counter" style={{ textAlign: widget.styles?.textAlign || 'center' }}>
            <div className="counter-number" style={{ fontSize: widget.styles?.fontSize || 48, fontWeight: 'bold', color: widget.content.color }}>
              {widget.content.prefix}{widget.content.number}{widget.content.suffix}
            </div>
            {widget.content.title && <div className="counter-title">{widget.content.title}</div>}
          </div>
        );
        
      case 'progress-bar':
        return (
          <div className="widget-progress">
            {widget.content.title && <div className="progress-title" style={{ marginBottom: '8px' }}>{widget.content.title}</div>}
            <div className="progress-bar-container" style={{ backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
              <div 
                className="progress-bar-fill"
                style={{ 
                  width: `${widget.content.percentage}%`,
                  backgroundColor: widget.content.color || '#007bff',
                  height: '30px',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff'
                }}
              >
                {widget.content.showLabel && <span>{widget.content.percentage}%</span>}
              </div>
            </div>
          </div>
        );
        
      case 'social-icons':
        return (
          <div className="widget-social-icons" style={{ display: 'flex', gap: '15px', justifyContent: widget.styles?.textAlign || 'center' }}>
            {widget.content.icons?.map((icon, index) => (
              <a key={index} href="#" style={{ fontSize: widget.content.size || 24, color: widget.content.color || '#333', textDecoration: 'none' }}>
                {getIconSymbol(icon)}
              </a>
            ))}
          </div>
        );
        
      // Form Elements (interactive)
      case 'input-text':
      case 'input-email':
      case 'input-password':
      case 'input-number':
        return (
          <div className="widget-form-field" style={{ position: 'relative' }}>
            {widget.content.label && (
              <label 
                style={{ 
                  display: 'block', 
                  marginBottom: widget.content.labelStyle?.marginBottom || '5px',
                  userSelect: 'none',
                  fontWeight: widget.content.labelStyle?.fontWeight || 'bold',
                  color: widget.content.labelStyle?.color || '#000',
                  fontSize: widget.content.labelStyle?.fontSize ? `${widget.content.labelStyle.fontSize}px` : '14px',
                  textAlign: widget.content.labelStyle?.textAlign || 'left'
                }}
              >
                {widget.content.label}
                {widget.content.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
              </label>
            )}
            <input
              type={widget.type.replace('input-', '')}
              placeholder={widget.content.placeholder}
              defaultValue={widget.content.value}
              required={widget.content.required}
              style={{ 
                width: '100%', 
                padding: widget.content.fieldStyle?.padding || '8px', 
                border: `1px solid ${widget.content.fieldStyle?.borderColor || '#ddd'}`, 
                borderRadius: `${widget.content.fieldStyle?.borderRadius || 4}px`,
                backgroundColor: widget.content.fieldStyle?.backgroundColor || '#f9f9f9',
                color: widget.content.fieldStyle?.color || '#333'
              }}
              readOnly
            />
            {widget.content.helpText && (
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px'
              }}>
                {widget.content.helpText}
              </div>
            )}
            {!widget.content.label && (
              <div 
                style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  color: '#999',
                  pointerEvents: 'none',
                  fontSize: '12px'
                }}
              >
                Open settings to edit field
              </div>
            )}
          </div>
        );
        
      case 'textarea':
        return (
          <div className="widget-form-field" style={{ position: 'relative' }}>
            {widget.content.label && (
              <label 
                style={{ 
                  display: 'block', 
                  marginBottom: widget.content.labelStyle?.marginBottom || '5px',
                  userSelect: 'none',
                  fontWeight: widget.content.labelStyle?.fontWeight || 'bold',
                  color: widget.content.labelStyle?.color || '#000',
                  fontSize: widget.content.labelStyle?.fontSize ? `${widget.content.labelStyle.fontSize}px` : '14px',
                  textAlign: widget.content.labelStyle?.textAlign || 'left'
                }}
              >
                {widget.content.label}
                {widget.content.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
              </label>
            )}
            <textarea
              placeholder={widget.content.placeholder}
              rows={widget.content.rows || 4}
              defaultValue={widget.content.value}
              style={{ 
                width: '100%', 
                padding: widget.content.fieldStyle?.padding || '8px', 
                border: `1px solid ${widget.content.fieldStyle?.borderColor || '#ddd'}`, 
                borderRadius: `${widget.content.fieldStyle?.borderRadius || 4}px`,
                backgroundColor: widget.content.fieldStyle?.backgroundColor || '#f9f9f9',
                color: widget.content.fieldStyle?.color || '#333',
                resize: 'vertical'
              }}
              readOnly
            />
            {widget.content.helpText && (
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px'
              }}>
                {widget.content.helpText}
              </div>
            )}
            {!widget.content.label && (
              <div 
                style={{ 
                  position: 'absolute', 
                  top: '30px', 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  color: '#999',
                  pointerEvents: 'none',
                  fontSize: '12px'
                }}
              >
                Click to edit field
              </div>
            )}
          </div>
        );
        
      case 'select':
        return (
          <div className="widget-form-field" style={{ position: 'relative' }}>
            {widget.content.label && (
              <label 
                style={{ 
                  display: 'block', 
                  marginBottom: widget.content.labelStyle?.marginBottom || '5px',
                  userSelect: 'none',
                  fontWeight: widget.content.labelStyle?.fontWeight || 'bold',
                  color: widget.content.labelStyle?.color || '#000',
                  fontSize: widget.content.labelStyle?.fontSize ? `${widget.content.labelStyle.fontSize}px` : '14px',
                  textAlign: widget.content.labelStyle?.textAlign || 'left'
                }}
              >
                {widget.content.label}
                {widget.content.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
              </label>
            )}
            <select 
              style={{ 
                width: '100%', 
                padding: widget.content.fieldStyle?.padding || '8px', 
                border: `1px solid ${widget.content.fieldStyle?.borderColor || '#ddd'}`, 
                borderRadius: `${widget.content.fieldStyle?.borderRadius || 4}px`,
                backgroundColor: widget.content.fieldStyle?.backgroundColor || '#f9f9f9',
                color: widget.content.fieldStyle?.color || '#333'
              }}
              disabled
            >
              <option value="">
                {widget.content.options && widget.content.options.length > 0 
                  ? 'Select an option' 
                  : 'Click to configure options'
                }
              </option>
              {widget.content.options?.map((option, index) => (
                <option key={index} value={option}>{option}</option>
              ))}
            </select>
            {widget.content.helpText && (
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px'
              }}>
                {widget.content.helpText}
              </div>
            )}
          </div>
        );
        
      case 'checkbox':
        return (
          <div className="widget-form-field" style={{ position: 'relative' }}>
            {widget.content.label && (
              <div style={{ marginBottom: '8px', fontWeight: 'bold', color: widget.content.labelStyle?.color || '#000', fontSize: widget.content.labelStyle?.fontSize ? `${widget.content.labelStyle.fontSize}px` : '14px', textAlign: widget.content.labelStyle?.textAlign || 'left' }}>
                {widget.content.label}
                {widget.content.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
              </div>
            )}
            {widget.content.options && widget.content.options.length > 0 ? (
              <div>
                {widget.content.options.map((option, index) => (
                  <label key={index} style={{ display: 'block', marginBottom: '5px', cursor: 'default' }}>
                    <input type="checkbox" defaultChecked={option.checked} disabled style={{ marginRight: '8px' }} /> 
                    {option.label}
                  </label>
                ))}
                {widget.content.helpText && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginTop: '8px'
                  }}>
                    {widget.content.helpText}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: '#999', fontSize: '12px' }}>
                Click to configure checkbox options
              </div>
            )}
          </div>
        );
        
      case 'radio':
        return (
          <div className="widget-form-field" style={{ position: 'relative' }}>
            {widget.content.label && (
              <div style={{ marginBottom: '8px', fontWeight: 'bold', color: widget.content.labelStyle?.color || '#000', fontSize: widget.content.labelStyle?.fontSize ? `${widget.content.labelStyle.fontSize}px` : '14px', textAlign: widget.content.labelStyle?.textAlign || 'left' }}>
                {widget.content.label}
                {widget.content.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
              </div>
            )}
            {widget.content.options && widget.content.options.length > 0 ? (
              <div>
                {widget.content.options.map((option, index) => (
                  <label key={index} style={{ display: 'block', marginBottom: '5px', cursor: 'default' }}>
                    <input type="radio" name={widget.content.name} defaultChecked={option.checked} disabled style={{ marginRight: '8px' }} /> 
                    {option.label}
                  </label>
                ))}
                {widget.content.helpText && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginTop: '8px'
                  }}>
                    {widget.content.helpText}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: '#999', fontSize: '12px' }}>
                Click to configure radio options
              </div>
            )}
          </div>
        );
        
      case 'toggle':
        return (
          <div className="widget-form-field">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="checkbox" defaultChecked={widget.content.checked} style={{ width: '40px', height: '20px' }} />
              {widget.content.label}
            </label>
          </div>
        );
        
      case 'color-picker':
        return (
          <div className="widget-form-field">
            {widget.content.label && <label style={{ display: 'block', marginBottom: '5px' }}>{widget.content.label}</label>}
            <input type="color" defaultValue={widget.content.value} />
          </div>
        );
        
      case 'form':
        return (
          <div className="widget-form">
            <form style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {widget.settings?.formFields?.map((field, index) => (
                <div key={field.id || index} className="form-field" style={{ width: field.width || '100%' }}>
                  {field.label && (
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      {field.label}
                    </label>
                  )}
                  
                  {field.type === 'text' || field.type === 'email' || field.type === 'password' || field.type === 'number' || field.type === 'date' || field.type === 'tel' ? (
                    <input
                      type={field.type}
                      placeholder={field.placeholder}
                      required={field.required}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  ) : field.type === 'textarea' ? (
                    <textarea
                      placeholder={field.placeholder}
                      rows={field.rows || 4}
                      required={field.required}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      required={field.required}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select an option</option>
                      {field.options?.map((option, optIndex) => (
                        <option key={optIndex} value={option.value || option}>
                          {option.label || option}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'radio' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {field.options?.map((option, optIndex) => (
                        <label key={optIndex} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="radio"
                            name={field.name || `radio_${field.id}`}
                            value={option.value || option}
                            defaultChecked={option.checked || false}
                            required={field.required}
                          />
                          {option.label || option}
                        </label>
                      ))}
                    </div>
                  ) : field.type === 'checkbox' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {field.options?.map((option, optIndex) => (
                        <label key={optIndex} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            name={field.name || `checkbox_${field.id}`}
                            value={option.value || option}
                            defaultChecked={option.checked || false}
                          />
                          {option.label || option}
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
              
              <div style={{ 
                textAlign: widget.settings?.submitButtonAlign || 'left',
                marginTop: '20px'
              }}>
                <button
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    backgroundColor: widget.settings?.submitButtonBackgroundColor || '#007bff',
                    color: widget.settings?.submitButtonTextColor || '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                  onMouseOut={(e) => e.target.style.backgroundColor = widget.settings?.submitButtonBackgroundColor || '#007bff'}
                >
                  {widget.settings?.submitButtonText || 'Submit'}
                </button>
              </div>
            </form>
          </div>
        );
    }
  };
  
  const renderEditor = () => {
    switch(widget.type) {
      case 'heading':
      case 'paragraph':
      case 'text':
        return (
          <input 
            type="text" 
            value={editValue.text !== undefined ? editValue.text : widget.content.text}
            onChange={(e) => setEditValue({ ...editValue, text: e.target.value })}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                onUpdate({ content: { ...widget.content, ...editValue } });
                setIsEditing(false);
                setEditValue({});
              }
            }}
            autoFocus
            placeholder="Enter text..."
          />
        );
        
      case 'button':
        return (
          <input 
            type="text" 
            value={editValue.text !== undefined ? editValue.text : widget.content.text}
            onChange={(e) => setEditValue({ ...editValue, text: e.target.value })}
            onBlur={() => {
              if (Object.keys(editValue).length > 0) {
                onUpdate({ content: { ...widget.content, ...editValue } });
              }
              setIsEditing(false);
              setEditValue({});
            }}
            autoFocus
            placeholder="Button text..."
          />
        );
        
      case 'image':
        return (
          <input 
            type="text" 
            value={editValue.url !== undefined ? editValue.url : widget.content.url}
            onChange={(e) => setEditValue({ ...editValue, url: e.target.value })}
            onBlur={() => {
              if (Object.keys(editValue).length > 0) {
                onUpdate({ content: { ...widget.content, ...editValue } });
              }
              setIsEditing(false);
              setEditValue({});
            }}
            autoFocus
            placeholder="Image URL..."
          />
        );
        
      case 'link':
        return (
          <div>
            <input 
              type="text" 
              value={editValue.text !== undefined ? editValue.text : widget.content.text}
              onChange={(e) => setEditValue({ ...editValue, text: e.target.value })}
              placeholder="Link text..."
              style={{ display: 'block', marginBottom: '8px' }}
            />
            <input 
              type="text" 
              value={editValue.url !== undefined ? editValue.url : widget.content.url}
              onChange={(e) => setEditValue({ ...editValue, url: e.target.value })}
              placeholder="URL..."
              style={{ display: 'block' }}
            />
          </div>
        );
        
      // Form Elements Inline Editing
      case 'input-text':
      case 'input-email':
      case 'input-password':
      case 'input-number':
        return (
          <div>
            <input 
              type="text" 
              value={editValue.label !== undefined ? editValue.label : widget.content.label}
              onChange={(e) => setEditValue({ ...editValue, label: e.target.value })}
              placeholder="Field label..."
              style={{ display: 'block', marginBottom: '8px', width: '100%', padding: '4px' }}
            />
            <input 
              type="text" 
              value={editValue.placeholder !== undefined ? editValue.placeholder : widget.content.placeholder}
              onChange={(e) => setEditValue({ ...editValue, placeholder: e.target.value })}
              placeholder="Placeholder text..."
              style={{ display: 'block', marginBottom: '8px', width: '100%', padding: '4px' }}
            />
            <input 
              type="text" 
              value={editValue.helpText !== undefined ? editValue.helpText : widget.content.helpText}
              onChange={(e) => setEditValue({ ...editValue, helpText: e.target.value })}
              placeholder="Help text..."
              style={{ display: 'block', width: '100%', padding: '4px' }}
            />
          </div>
        );
        
      case 'textarea':
        return (
          <div>
            <input 
              type="text" 
              value={editValue.label !== undefined ? editValue.label : widget.content.label}
              onChange={(e) => setEditValue({ ...editValue, label: e.target.value })}
              placeholder="Field label..."
              style={{ display: 'block', marginBottom: '8px', width: '100%', padding: '4px' }}
            />
            <input 
              type="text" 
              value={editValue.placeholder !== undefined ? editValue.placeholder : widget.content.placeholder}
              onChange={(e) => setEditValue({ ...editValue, placeholder: e.target.value })}
              placeholder="Placeholder text..."
              style={{ display: 'block', marginBottom: '8px', width: '100%', padding: '4px' }}
            />
            <input 
              type="text" 
              value={editValue.helpText !== undefined ? editValue.helpText : widget.content.helpText}
              onChange={(e) => setEditValue({ ...editValue, helpText: e.target.value })}
              placeholder="Help text..."
              style={{ display: 'block', width: '100%', padding: '4px' }}
            />
          </div>
        );
        
      case 'select':
        return (
          <div>
            <input 
              type="text" 
              value={editValue.label !== undefined ? editValue.label : widget.content.label}
              onChange={(e) => setEditValue({ ...editValue, label: e.target.value })}
              placeholder="Field label..."
              style={{ display: 'block', marginBottom: '8px', width: '100%', padding: '4px' }}
            />
            <textarea 
              value={editValue.options ? editValue.options.join('\n') : (widget.content.options ? widget.content.options.join('\n') : '')}
              onChange={(e) => setEditValue({ ...editValue, options: e.target.value.split('\n').filter(opt => opt.trim()) })}
              placeholder="Options (one per line)..."
              rows={4}
              style={{ display: 'block', marginBottom: '8px', width: '100%', padding: '4px' }}
            />
            <input 
              type="text" 
              value={editValue.helpText !== undefined ? editValue.helpText : widget.content.helpText}
              onChange={(e) => setEditValue({ ...editValue, helpText: e.target.value })}
              placeholder="Help text..."
              style={{ display: 'block', width: '100%', padding: '4px' }}
            />
          </div>
        );
        
      case 'checkbox':
      case 'radio':
        return (
          <div>
            <input 
              type="text" 
              value={editValue.label !== undefined ? editValue.label : widget.content.label}
              onChange={(e) => setEditValue({ ...editValue, label: e.target.value })}
              placeholder="Field label..."
              style={{ display: 'block', marginBottom: '8px', width: '100%', padding: '4px' }}
            />
            <textarea 
              value={editValue.options ? editValue.options.map(opt => opt.label || opt).join('\n') : (widget.content.options ? widget.content.options.map(opt => opt.label || opt).join('\n') : '')}
              onChange={(e) => {
                const options = e.target.value.split('\n').filter(opt => opt.trim()).map(opt => ({ label: opt, value: opt.toLowerCase().replace(/\s+/g, '_') }));
                setEditValue({ ...editValue, options });
              }}
              placeholder="Options (one per line)..."
              rows={4}
              style={{ display: 'block', marginBottom: '8px', width: '100%', padding: '4px' }}
            />
            <input 
              type="text" 
              value={editValue.helpText !== undefined ? editValue.helpText : widget.content.helpText}
              onChange={(e) => setEditValue({ ...editValue, helpText: e.target.value })}
              placeholder="Help text..."
              style={{ display: 'block', width: '100%', padding: '4px' }}
            />
          </div>
        );
        
      case 'form':
        return (
          <div>
            <p>Form editing is handled through the settings panel. Click the settings icon to configure form fields.</p>
          </div>
        );
        
      default:
        return <div>Edit mode not available for this widget type</div>;
    }
  };
  
  return (
    <div 
      className={`widget-renderer ${isSelected ? 'selected' : ''}`}
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.setData('widgetId', widget.id);
        if (sectionId) {
          e.dataTransfer.setData('sourceSectionId', sectionId);
        }
        if (columnId) {
          e.dataTransfer.setData('sourceColumnId', columnId);
        }
      }}
      onClick={onSelect}
      onDoubleClick={() => {
        if (allowInlineEdit(widget.type)) {
          setIsEditing(true);
        } else if (onOpenSettings) {
          onOpenSettings(widget);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        border: isSelected ? '2px solid #007bff' : '2px solid transparent',
        margin: '4px 0',
        padding: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
    >

      <div 
        className="widget-controls"
        style={{
          position: 'absolute',
          top: '-30px',
          right: '0',
          display: isSelected || isHovered ? 'flex' : 'none',
          gap: '5px',
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '4px',
          zIndex: 1000
        }}
      >
        {allowInlineEdit(widget.type) && (
          <button 
            className="widget-control"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(!isEditing);
              setEditValue({});
            }}
            style={{
              padding: '4px 8px',
              border: 'none',
              backgroundColor: '#f0f0f0',
              cursor: 'pointer',
              borderRadius: '3px'
            }}
          >
            ✏️ Edit
          </button>
        )}
        <button 
          className="widget-control settings"
          onClick={(e) => {
            e.stopPropagation();
            if (onOpenSettings) {
              onOpenSettings(widget);
            }
          }}
          style={{
            padding: '4px 8px',
            border: 'none',
            backgroundColor: '#17a2b8',
            color: '#fff',
            cursor: 'pointer',
            borderRadius: '3px'
          }}
        >
          ⚙️ Settings
        </button>
        <button 
          className="widget-control delete"
          onClick={(e) => {
            e.stopPropagation();
            Swal.fire({
              title: 'Are you sure?',
              text: 'You won\'t be able to revert this!',
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#dc3545',
              cancelButtonColor: '#6c757d',
              confirmButtonText: 'Yes, delete it!'
            }).then((result) => {
              if (result.isConfirmed) {
                onDelete();
              }
            });
          }}
          style={{
            padding: '4px 8px',
            border: 'none',
            backgroundColor: '#dc3545',
            color: '#fff',
            cursor: 'pointer',
            borderRadius: '3px'
          }}
        >
          🗑️ Delete
        </button>
        <div 
          className="widget-drag-handle"
          style={{
            padding: '4px 8px',
            cursor: 'grab',
            backgroundColor: '#f0f0f0',
            borderRadius: '3px'
          }}
        >
          ⋮⋮
        </div>
      </div>
      
      {isEditing ? (
        <div 
          className="widget-editor"
          style={{
            padding: '10px',
            backgroundColor: '#f9f9f9',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        >
          {renderEditor()}
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                if (Object.keys(editValue).length > 0) {
                  onUpdate({ content: { ...widget.content, ...editValue } });
                }
                setIsEditing(false);
                setEditValue({});
              }}
              style={{ padding: '4px 12px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
            >
              Save
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setEditValue({});
              }}
              style={{ padding: '4px 12px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="widget-content">
          {renderWidget()}
        </div>
      )}
    </div>
  );
};

export default WidgetRenderer;